import { useEffect, useId, useRef, useState } from "react";
import { ErrorNotice } from "../components/AdminFeedback";
import { FieldRequirement } from "../components/FieldRequirement";
import { ValidatedForm } from "../components/ValidatedForm";
import { AppSelect } from "../components/inputs/AppSelect";
import { useLocale } from "../i18n";
import type { TeamRole } from "./TeamRoleEditor";
import type { RecordValue } from "./types";
import { useSetupMutation } from "./useSetupMutation";

type Review = { individualId: string; roleCode: string; roleName: string; expiresAtEpochMillis: number };
export function TeamAccountInvite({ partyId, partyName, unitId, version, roles, onSaved, onClose, onLock }: {
  partyId: string; partyName: string; unitId: string; version: number; roles: TeamRole[];
  onSaved: (result: RecordValue) => void; onClose: () => void; onLock: (locked: boolean) => void;
}) {
  const { w } = useLocale(); const id = useId(); const dialog = useRef<HTMLDialogElement>(null); const running = useRef(false);
  const [individualId, setIndividualId] = useState(""); const [roleCode, setRoleCode] = useState("");
  const [review, setReview] = useState<Review>();
  const mutation = useSetupMutation<RecordValue>(onSaved);
  const locked = mutation.busy || mutation.uncertain;
  useEffect(() => { onLock(locked); return () => onLock(false); }, [locked, onLock]);
  useEffect(() => {
    const current = dialog.current;
    if (review) current?.showModal();
    return () => current?.close();
  }, [review]);
  async function send(retry = false) {
    if (running.current || mutation.busy || (!retry && mutation.uncertain) || !review) return;
    running.current = true;
    try {
      if (retry) await mutation.send();
      else await mutation.send("/events/partyGroup/invitation?" + new URLSearchParams({ partyId, unitId, individualId: review.individualId }), "POST", {
        expectedVersion: version, roleCodes: [review.roleCode], expiresAtEpochMillis: review.expiresAtEpochMillis,
      });
    } finally { running.current = false; }
  }
  return <section className="event-team-member-form">
    <h4>{w("دعوة عضو بمعرّف الحساب", "Invite by account ID")}</h4>
    <p className="event-team-search-help">{w("أدخل معرّف الحساب الذي شاركه معك العضو واختر دوره. تصل الدعوة إلى حسابه، ولا تصبح العضوية فعالة إلا بعد قبوله.", "Enter the account ID shared with you by the member and choose their role. The invitation appears in their account; membership starts only after they accept.")}</p>
    <ValidatedForm valid={roles.some(role => role.code === roleCode)} validationMessage={w("اختر دوراً من أدوار هذا الفريق.", "Choose a role defined for this team.")} onSubmit={event => {
      event.preventDefault();
      const role = roles.find(item => item.code === roleCode);
      if (!locked && role) setReview({ individualId: individualId.trim(), roleCode, roleName: role.name, expiresAtEpochMillis: Date.now() + 48 * 60 * 60 * 1000 });
    }}>
      <fieldset disabled={locked}>
        <div className="event-team-role-choice">
          <label htmlFor={`${id}-account`}>{w("معرّف الحساب", "Account ID")} <FieldRequirement required /></label>
          <input id={`${id}-account`} required maxLength={96} pattern={"[A-Za-z0-9][A-Za-z0-9_.\\-]{0,95}"} dir="ltr" autoComplete="off" value={individualId} onChange={event => setIndividualId(event.target.value)} />
          <label htmlFor={`${id}-role`}>{w("دور العضو", "Member role")} <FieldRequirement required /></label>
          <AppSelect id={`${id}-role`} required value={roleCode} onChange={setRoleCode}>
            <option value="">{w("اختر الدور", "Select role")}</option>
            {roles.map(role => <option key={role.code} value={role.code}>{role.name}</option>)}
          </AppSelect>
        </div>
        <div className="event-team-actions">
          <button type="submit" className="primary">{w("مراجعة الدعوة", "Review invitation")}</button>
          <button type="button" className="secondary" onClick={onClose}>{w("إلغاء", "Cancel")}</button>
        </div>
      </fieldset>
    </ValidatedForm>
    {review && <dialog ref={dialog} className="action-dialog" aria-labelledby={`${id}-review`} onCancel={event => { event.preventDefault(); if (!locked) setReview(undefined); }}>
      <div className="dialog-header"><h2 id={`${id}-review`}>{w("تأكيد دعوة عضو", "Confirm team invitation")}</h2></div>
      <p>{w("الفريق", "Team")}: <strong>{partyName}</strong></p>
      <p>{w("معرّف الحساب", "Account ID")}: <bdi style={{ overflowWrap: "anywhere" }}>{review.individualId}</bdi></p>
      <p>{w("دور العضو", "Member role")}: <strong>{review.roleName}</strong></p>
      <p className="muted">{w("راجع المعرّف والدور قبل التأكيد. تبقى الدعوة بانتظار قبول المستلم لمدة 48 ساعة، ولا تُرسل رسالة نصية.", "Review the ID and role before confirming. The invitation awaits the recipient’s acceptance for 48 hours; no SMS is sent.")}</p>
      <ErrorNotice error={mutation.error} />
      <div className="dialog-actions">
        <button type="button" className="secondary" disabled={locked} onClick={() => setReview(undefined)}>{w("رجوع للتعديل", "Back to edit")}</button>
        {mutation.uncertain
          ? <button type="button" className="primary" disabled={mutation.busy} onClick={() => void send(true)}>{w("إعادة محاولة إرسال الدعوة", "Retry invitation")}</button>
          : <button type="button" className="primary" disabled={mutation.busy} onClick={() => void send()}>{mutation.busy ? w("جارٍ الإرسال…", "Sending…") : w("تأكيد إرسال الدعوة", "Confirm invitation")}</button>}
      </div>
    </dialog>}
  </section>;
}
